import { PDFFont, PDFPage } from 'pdf-lib';

/**
 * The AWB / invoice PDFs are drawn with the standard 14 fonts (Helvetica), which are
 * WinAnsi (cp1252) encoded. pdf-lib throws as soon as it meets a character outside that
 * range — a single full-width comma pasted from a Chinese IME aborts the whole render
 * with `WinAnsi cannot encode "，" (0xff0c)`.
 *
 * Rather than lose the document, fold what has a Latin-1 equivalent (full-width forms,
 * CJK punctuation) and drop the rest. Text drawn with an embedded Unicode font is left
 * untouched — see `installWinAnsiFallback`.
 */

// U+FF01..U+FF5E are the full-width forms of ASCII 0x21..0x7E.
const FULLWIDTH_FIRST = 0xff01;
const FULLWIDTH_LAST = 0xff5e;
const FULLWIDTH_OFFSET = 0xfee0;

const PUNCTUATION: Record<string, string> = {
  '　': ' ', // ideographic space
  '、': ', ', // 、
  '。': '. ', // 。
  '「': '"', // 「
  '」': '"', // 」
  '『': '"', // 『
  '』': '"', // 』
  '〈': '<', // 〈
  '〉': '>', // 〉
  '《': '<<', // 《
  '》': '>>', // 》
  '【': '[', // 【
  '】': ']', // 】
  '〔': '(', // 〔
  '〕': ')', // 〕
  '｟': '((', // ｟
  '｠': '))', // ｠
  '・': '.', // ・
  '‧': '.', // ‧
};

// The cp1252 characters that sit in the 0x80-0x9F gap of Latin-1.
const CP1252_EXTRAS = '€‚ƒ„…†‡ˆ‰Š‹ŒŽ‘’“”•–—˜™š›œžŸ';

// NBSP, en/em/thin/hair spaces, narrow NBSP, medium math space, ideographic space.
const UNICODE_SPACES = /[\u00a0\u2000-\u200a\u202f\u205f\u3000]/;

function isWinAnsiEncodable(char: string): boolean {
  // drawText handles these itself before reaching the encoder.
  if (char === '\n' || char === '\r' || char === '\t') return true;

  const code = char.charCodeAt(0);
  if (code >= 0x20 && code <= 0x7e) return true;
  if (code >= 0xa0 && code <= 0xff) return true;

  return CP1252_EXTRAS.indexOf(char) !== -1;
}

/** Best-effort conversion of `text` into something the standard 14 fonts can draw. */
export function toWinAnsi(text: string): string {
  if (typeof text !== 'string' || text === '') return text;

  let result = '';
  let dropped = '';

  for (const char of text) {
    // Non-breaking and other Unicode spaces (common in text pasted from Word, web pages or
    // chat apps) look like spaces but are not word breaks, so maxWidth wrapping never splits
    // the line and it runs into the next column. Draw them as plain spaces.
    if (UNICODE_SPACES.test(char)) {
      result += ' ';
      continue;
    }

    if (isWinAnsiEncodable(char)) {
      result += char;
      continue;
    }

    const folded = PUNCTUATION[char];
    if (folded !== undefined) {
      result += folded;
      continue;
    }

    const code = char.codePointAt(0) ?? 0;
    if (code >= FULLWIDTH_FIRST && code <= FULLWIDTH_LAST) {
      result += String.fromCharCode(code - FULLWIDTH_OFFSET);
      continue;
    }

    // Nothing sensible to fall back to (CJK ideographs, emoji, …). Helvetica has no
    // glyph for these anyway, so drop them instead of aborting the document.
    dropped += char;
  }

  if (dropped) {
    console.warn(`[pdf] dropped characters the PDF font cannot draw: "${dropped}" (in "${text}")`);
  }

  return result;
}

// A font only needs sanitising if it genuinely cannot encode non-Latin-1 text. Probing is
// more reliable than inspecting the embedder class, which minification renames.
const sanitisingNeeded = new WeakMap<PDFFont, boolean>();

function fontNeedsSanitising(font: PDFFont | undefined, widthOfTextAtSize: PDFFont['widthOfTextAtSize']): boolean {
  // No font resolved: page.drawText() falls back to Helvetica, which does need it.
  if (!font) return true;

  const cached = sanitisingNeeded.get(font);
  if (cached !== undefined) return cached;

  let needed: boolean;
  try {
    widthOfTextAtSize.call(font, '，', 8);
    needed = false;
  } catch {
    needed = true;
  }

  sanitisingNeeded.set(font, needed);
  return needed;
}

let installed = false;

/**
 * Patch pdf-lib so text drawn with a WinAnsi-only font is folded to Latin-1 instead of
 * throwing. Embedded Unicode fonts are detected by probing and left alone, so this is a
 * no-op the day we start embedding a CJK font.
 */
export function installWinAnsiFallback(): void {
  if (installed) return;
  installed = true;

  const originalDrawText = PDFPage.prototype.drawText;
  const originalWidthOfTextAtSize = PDFFont.prototype.widthOfTextAtSize;

  PDFPage.prototype.drawText = function drawText(text, options) {
    const font = options?.font ?? (this as any).font;
    const safeText = fontNeedsSanitising(font, originalWidthOfTextAtSize) ? toWinAnsi(text) : text;
    return originalDrawText.call(this, safeText, options);
  };

  PDFFont.prototype.widthOfTextAtSize = function widthOfTextAtSize(text, size) {
    const safeText = fontNeedsSanitising(this, originalWidthOfTextAtSize) ? toWinAnsi(text) : text;
    return originalWidthOfTextAtSize.call(this, safeText, size);
  };
}
